/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import InputTextLine from '../../core/InputTextLine';
import Toggle from '../../core/Toggle';
import Tooltip from '../../core/Tooltip';
import IconButton from '../../core/IconButton';
import Select from '../../core/Select';
import RangeBar from '../../core/RangeBar';
import Button from '../../core/Button';
import Badge from '../../core/Badge';
import ColorPicker from '../../core/ColorPicker2';

import { trimString } from '../../utils/functions/formatString';

import {
  ExpandMore, Underline, Italic, Bold, FontColor, LineColor, BackgroundColor, Add, Close, SortArrow,
} from '../../icons';

import {
  ControlContainer,
  LabelController,
  FlexRow,
  NumberControls,
  OrderControls,
  OrderBtn,
} from './styled/Base.styled';

const basePropTypes = {
  label: PropTypes.string,
  handle: PropTypes.func,
  kWord: PropTypes.string,
  configValue: PropTypes.any,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
};

const baseDefaultProps = {
  label: '',
  handle: () => {},
  kWord: '',
  configValue: undefined,
  fullWidth: false,
  disabled: false,
};

const periodReplacer = (isNumber, val) => (isNumber ? val.toString().replace('.', ',') : val);
// const commaReplacer = (isNumber, val) => (isNumber ? val.toString().replace(',', '.') : val);

/**
 * Tradicional input line that handles strings or numbers
 */
export const InputLineController = ({
  isNumber,
  min,
  max,
  defaultNumber,
  step,
  precision,
  handle,
  kWord,
  label,
  configValue,
  orientation,
  fullWidth,
  disabled,
  placeholder,
}) => {
  const cron = useRef();
  const [val, setVal] = useState(periodReplacer(isNumber, (configValue || (isNumber ? defaultNumber : ''))));

  const handleConfigVal = (value) => {
    clearTimeout(cron.current);
    cron.current = setTimeout(() => {
      handle(trimString(value.toString()), kWord);
    }, 200);
  };

  const handleOpsBtns = (value = '', op = 0) => {
    value = value.toString().replace(',', '.');
    const numericVal = Math.max(Math.min((Number(value) || 0) + op, max), min).toFixed(precision);
    setVal(periodReplacer(isNumber, numericVal));
    handleConfigVal(numericVal);
  };

  const handleVal = (value = '') => {
    if (isNumber) {
      const regex = new RegExp(`^-{0,1}\\d*${precision > 0 ? `,{0,1}\\d{0,${precision}}` : ''}`);
      [value] = value.toString().match(regex);
      setVal(value);
      value = value.replace(',', '.');
      const numericVal = (Number(value) || 0);
      if (value && !Number.isNaN(+value)) {
        value = Math.max(Math.min(numericVal, max), min);
      } else {
        value = defaultNumber;
      }
    } else {
      setVal(value);
    }
    handleConfigVal(value);
  };

  return (
    <ControlContainer
      orientation={orientation}
      fullWidth={fullWidth}
      isNumber={isNumber}
      disabled={disabled}
    >
      <LabelController htmlFor={kWord}>{label}</LabelController>
      <FlexRow style={{ width: '100%' }}>
        <InputTextLine
          id={kWord}
          value={val}
          onChange={(e) => handleVal(e.target.value)}
          onBlur={() => setVal(periodReplacer(isNumber, (configValue || '')))}
          label=""
          placeholder={placeholder}
        />
        {isNumber && (
          <NumberControls>
            <button type="button" className="number-increase" onClick={() => handleOpsBtns(val, step)}><ExpandMore /></button>
            <button type="button" className="number-decrease" onClick={() => handleOpsBtns(val, -step)}><ExpandMore /></button>
          </NumberControls>
        )}
      </FlexRow>
    </ControlContainer>
  );
};

InputLineController.propTypes = {
  ...basePropTypes,
  isNumber: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  defaultNumber: PropTypes.number,
  step: PropTypes.number,
  precision: PropTypes.number,
  orientation: PropTypes.oneOf(['row', 'column']),
  placeholder: PropTypes.string,
};

InputLineController.defaultProps = {
  ...baseDefaultProps,
  isNumber: false,
  min: -Infinity,
  max: Infinity,
  defaultNumber: 0,
  step: 1,
  precision: 0,
  orientation: 'column',
  placeholder: '',
};

/**
 * Toggle input in a switch style
 */
export const ToggleSwitchController = ({
  handle,
  kWord,
  label,
  configValue,
  firstOption,
  secondOption,
  firstColor,
  secondColor,
  fullWidth,
  disabled,
}) => {
  const theme = useTheme();
  return (
    <ControlContainer
      className={fullWidth ? '' : 'fullwidth-inline'}
      orientation={fullWidth ? 'row' : 'column'}
      fullWidth={fullWidth}
      disabled={disabled}
    >
      <LabelController htmlFor={kWord}>{label}</LabelController>
      <Toggle
        label=""
        checked={configValue || false}
        handleChange={() => handle(!configValue, kWord)}
        styleOption={{ color: theme.grey, fontSize: 14 }}
        styleContainer={{ margin: 'auto 0' }}
        highlightOption={false}
        secondOption={secondOption}
        firstOption={firstOption}
        firstColor={firstColor}
        secondColor={secondColor}
        size="small"
        optionPosition={fullWidth ? 'left' : 'right'}
      />
    </ControlContainer>
  );
};

ToggleSwitchController.propTypes = {
  ...basePropTypes,
  firstOption: PropTypes.string,
  secondOption: PropTypes.string,
  firstColor: PropTypes.string,
  secondColor: PropTypes.string,
};

ToggleSwitchController.defaultProps = {
  ...baseDefaultProps,
  firstOption: '',
  secondOption: '',
  firstColor: undefined,
  secondColor: undefined,
};

/**
 * Button controllers inline
 */
export const ButtonLineController = ({
  handle,
  boldVal,
  boldKWord,
  italicVal,
  italicKWord,
  underlineVal,
  underlineKWord,
  fontColorVal,
  fontColorKWord,
  lineColorVal,
  lineColorKWord,
  backgroundColorVal,
  backgroundColorKWord,
  fullWidth,
  disabled,
}) => {
  const cron = useRef();

  const handleColorVal = (value, kWord) => {
    clearTimeout(cron.current);
    cron.current = setTimeout(() => {
      handle(value, kWord);
    }, 200);
  };

  return (
    <ControlContainer
      orientation="row"
      fullWidth={fullWidth}
      style={{ gap: '5px' }}
      disabled={disabled}
    >
      <FlexRow style={{ width: '100%', gap: '5px', justifyContent: 'start' }}>
        {boldKWord && (
          <Tooltip text="Negrito" direction="right">
            <IconButton
              shape="rounded"
              variant={!boldVal ? 'outlined' : 'contained'}
              onClick={() => handle(!boldVal, boldKWord)}
            >
              <Bold />
            </IconButton>
          </Tooltip>
        )}
        {italicKWord && (
          <Tooltip text="Itálico" direction="right">
            <IconButton
              shape="rounded"
              variant={!italicVal ? 'outlined' : 'contained'}
              onClick={() => handle(!italicVal, italicKWord)}
            >
              <Italic />
            </IconButton>
          </Tooltip>
        )}
        {underlineKWord && (
          <Tooltip text="Sublinhado" direction="right">
            <IconButton
              shape="rounded"
              variant={!underlineVal ? 'outlined' : 'contained'}
              onClick={() => handle(!underlineVal, underlineKWord)}
            >
              <Underline />
            </IconButton>
          </Tooltip>
        )}
      </FlexRow>
      <FlexRow style={{ width: '100%', gap: '5px', justifyContent: 'end' }}>
        {fontColorKWord && (
          <Tooltip text="Cor da fonte" direction="right">
            <ColorPicker
              onChange={(val) => handleColorVal(val, fontColorKWord)}
              color={fontColorVal}
              isIconButton
            >
              <FontColor />
            </ColorPicker>
          </Tooltip>
        )}
        {lineColorKWord && (
          <Tooltip text="Cor da linha" direction="right">
            <ColorPicker
              onChange={(val) => handleColorVal(val, lineColorKWord)}
              color={lineColorVal}
              isIconButton
            >
              <LineColor />
            </ColorPicker>
          </Tooltip>
        )}
        {backgroundColorKWord && (
          <Tooltip text="Cor de fundo" direction="right">
            <ColorPicker
              onChange={(val) => handleColorVal(val, backgroundColorKWord)}
              color={backgroundColorVal}
              isIconButton
            >
              <BackgroundColor />
            </ColorPicker>
          </Tooltip>
        )}
      </FlexRow>
    </ControlContainer>
  );
};

ButtonLineController.propTypes = {
  handle: PropTypes.func,
  boldVal: PropTypes.bool,
  boldKWord: PropTypes.string,
  italicVal: PropTypes.bool,
  italicKWord: PropTypes.string,
  underlineVal: PropTypes.bool,
  underlineKWord: PropTypes.string,
  fontColorVal: PropTypes.string,
  fontColorKWord: PropTypes.string,
  lineColorVal: PropTypes.string,
  lineColorKWord: PropTypes.string,
  backgroundColorVal: PropTypes.string,
  backgroundColorKWord: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
};

ButtonLineController.defaultProps = {
  handle: () => {},
  boldVal: false,
  boldKWord: '',
  italicVal: false,
  italicKWord: '',
  underlineVal: false,
  underlineKWord: '',
  fontColorVal: '',
  fontColorKWord: '',
  lineColorVal: '',
  lineColorKWord: '',
  backgroundColorVal: '',
  backgroundColorKWord: '',
  disabled: false,
  fullWidth: false,
};

/**
 * Tradicional select controller for multiple options
 */
export const SelectController = ({
  handle,
  kWord,
  label,
  configValue,
  options,
  fullWidth,
  disabled,
}) => (
  <ControlContainer fullWidth={fullWidth} disabled={disabled}>
    <LabelController htmlFor={kWord}>{label}</LabelController>
    <Select
      selectLabel=""
      placeholder="Selecione"
      options={options}
      value={{ label: configValue, value: configValue }}
      onChange={(selected) => handle(selected.value, kWord)}
      fullWidth
    />
  </ControlContainer>
);

SelectController.propTypes = {
  ...basePropTypes,
  options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
};

SelectController.defaultProps = {
  ...baseDefaultProps,
  options: [],
};

/**
 * Input range to handle numerical values accurately
 */
export const RangeController = ({
  min,
  max,
  step,
  precision,
  handle,
  kWord,
  label,
  configValue,
  fullWidth,
  disabled,
}) => {
  const cron = useRef();
  const [val, setVal] = useState((configValue || 0).toString().replace('.', ','));

  const handleConfigVal = (value) => {
    clearTimeout(cron.current);
    cron.current = setTimeout(() => {
      handle(trimString(value.toString()), kWord);
    }, 200);
  };

  const handleOpsBtns = (value = '', op = 0) => {
    value = value.toString().replace(',', '.');
    const numericVal = Math.max(Math.min((Number(value) || 0) + op, max), min).toFixed(precision);
    setVal(numericVal.toString().replace('.', ','));
    handleConfigVal(numericVal);
  };

  const handleVal = (value = '') => {
    const regex = new RegExp(`^-{0,1}\\d*${precision > 0 ? `,{0,1}\\d{0,${precision}}` : ''}`);
    [value] = value.toString().match(regex);
    setVal(value);
    value = value.replace(',', '.');
    const numericVal = (Number(value) || 0);
    if (value && !Number.isNaN(+value)) {
      value = Math.max(Math.min(numericVal, max), min);
    } else {
      value = min;
    }
    handleConfigVal(value);
  };

  return (
    <ControlContainer fullWidth={fullWidth} disabled={disabled} isNumber>
      <LabelController htmlFor={kWord}>{label}</LabelController>
      <FlexRow style={{ width: '100%', alignItems: 'center', gap: '10px' }}>
        <FlexRow style={{ width: '35%', position: 'relative' }}>
          <InputTextLine
            value={val}
            onChange={(e) => handleVal(e.target.value)}
            onBlur={() => setVal((configValue || 0).toString().replace('.', ','))}
            // wrapperStyle={{ width: '30%' }}
            style={{ textAlign: 'center' }}
          />
          <NumberControls>
            <button type="button" className="number-increase" onClick={() => handleOpsBtns(val, step)}><ExpandMore /></button>
            <button type="button" className="number-decrease" onClick={() => handleOpsBtns(val, -step)}><ExpandMore /></button>
          </NumberControls>
        </FlexRow>
        <RangeBar
          min={min}
          max={max}
          step={step}
          value={val.toString().replace(',', '.')}
          onChange={(value) => handleVal(value.toString().replace('.', ','))}
          // onBlur={() => setVal((configValue || '').toString().replace('.', ','))}
        />
      </FlexRow>
    </ControlContainer>
  );
};

RangeController.propTypes = {
  ...basePropTypes,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  precision: PropTypes.number,
};

RangeController.defaultProps = {
  ...baseDefaultProps,
  min: 0,
  max: Infinity,
  step: 1,
  precision: 0,
};

/**
 * A selector for few options
 */
export const ChoiceBoxController = ({
  handle,
  kWord,
  label,
  configValue,
  options,
  fullWidth,
  disabled,
}) => {
  const theme = useTheme();
  return (
    <ControlContainer fullWidth={fullWidth} disabled={disabled}>
      <LabelController htmlFor={kWord}>{label}</LabelController>
      <FlexRow style={{
        width: '100%',
        alignItems: 'center',
        boxShadow: `0px 2px 4px 0px ${theme.shadow}`,
        borderRadius: '8px',
        height: '100%',
        marginTop: '3px',
      }}
      >
        {options.map((opt) => (
          <Button
            key={opt.id + opt.label}
            shape="square"
            size="small"
            className="choicebox-btn"
            variant={configValue === opt.value ? 'contained' : 'outlined'}
            onClick={() => handle(opt.value, kWord)}
          >
            {opt.label}
          </Button>
        ))}
      </FlexRow>
    </ControlContainer>
  );
};

ChoiceBoxController.propTypes = {
  ...basePropTypes,
  options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
};

ChoiceBoxController.defaultProps = {
  ...baseDefaultProps,
  options: [],
};

/**
 * A single color picker inline
 */
export const SimpleColorPickerController = ({
  handle,
  kWord,
  label,
  configValue,
  fullWidth,
  orientation,
  disabled,
  dataColorTarget,
}) => {
  const cron = useRef();

  const handleColorVal = (value) => {
    clearTimeout(cron.current);
    cron.current = setTimeout(() => {
      handle((dataColorTarget ? { value, target: dataColorTarget } : value), kWord);
    }, 200);
  };

  return (
    <ControlContainer
      orientation={orientation}
      fullWidth={fullWidth}
      disabled={disabled}
    >
      <LabelController htmlFor={kWord}>{label}</LabelController>
      <ColorPicker
        onChange={(val) => handleColorVal(val)}
        color={configValue}
        style={{ height: '32.5px', minWidth: '60px' }}
      />
    </ControlContainer>
  );
};

SimpleColorPickerController.propTypes = {
  ...basePropTypes,
  orientation: PropTypes.oneOf(['row', 'column']),
  dataColorTarget: PropTypes.string,
};

SimpleColorPickerController.defaultProps = {
  ...baseDefaultProps,
  orientation: 'column',
  dataColorTarget: undefined,
};

/**
 * A controller to customize the ordering of data
 */
export const CustomSortController = ({
  handle,
  kWord,
  oldOrder,
  newOrder,
  removeTotal,
  disabled,
}) => {
  useEffect(() => {
    if (newOrder.length === 0 || newOrder.length !== oldOrder.length - (removeTotal ? 1 : 0)) {
      handle({
        kpiCategories: oldOrder.reduce((aux, item) => {
          const fItem = typeof item === 'string' ? item : item[item.length - 1];
          if (!(removeTotal && fItem === 'Totais')) aux.push(fItem);
          return aux;
        }, []),
      }, kWord);
    }
  }, [newOrder]);

  const changePosition = (pos, op) => {
    const newPos = pos + op;
    if (newPos < 0 && newPos >= newOrder.lenght) return;
    const tempList = [...newOrder];
    const [auxItem] = tempList.splice(pos, 1);
    tempList.splice(newPos, 0, auxItem);
    handle({ kpiCategories: [...tempList] }, kWord);
  };

  return (
    <ControlContainer
      fullWidth
      disabled={disabled}
      fullHeight
    >
      <FlexRow style={{ width: '100%', gap: '10px' }}>
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          alignItems: 'center',
        }}
        >
          <LabelController htmlFor={kWord}>Ordem customizada</LabelController>
          {newOrder.map((item, i) => (
            <FlexRow style={{ width: '100%', gap: '10px' }} key={`ini-${item}`}>
              <OrderControls
                disableFirst={i === 0}
                disableLast={(newOrder.length - 1) === i}
              >
                <Tooltip text="Subir de posição" direction="right">
                  <OrderBtn className="order-increase" onClick={() => changePosition(i, -1)}><SortArrow /></OrderBtn>
                </Tooltip>
                <Tooltip text="Descer de posição" direction="right">
                  <OrderBtn className="order-decrease" onClick={() => changePosition(i, 1)}><SortArrow /></OrderBtn>
                </Tooltip>
              </OrderControls>
              <Button
                shape="square"
                size="small"
                variant="contained"
                // disabled={isTotal}
                // onClick={() => addInSort(item)}
                style={{ width: '100%', cursor: 'default' }}
              >
                {item}
              </Button>
            </FlexRow>
          ))}
        </div>
      </FlexRow>
    </ControlContainer>
  );
};

CustomSortController.propTypes = {
  handle: PropTypes.func,
  kWord: PropTypes.string,
  oldOrder: PropTypes.arrayOf(PropTypes.any),
  newOrder: PropTypes.arrayOf(PropTypes.any),
  disabled: PropTypes.bool,
  removeTotal: PropTypes.bool,
};

CustomSortController.defaultProps = {
  handle: () => {},
  kWord: '',
  oldOrder: [],
  newOrder: [],
  disabled: false,
  removeTotal: false,
};

// antigo
/* export const CustomSortController = ({
  handle,
  kWord,
  oldOrder,
  newOrder,
  removeTotal,
  disabled,
}) => {
  const removeFromSort = (item) => {
    if (newOrder.includes(item)) {
      handle({ kpiCategories: [...newOrder.filter((i) => i !== item)] }, kWord);
    }
  };

  const addInSort = (item) => {
    if (!newOrder.includes(item)) {
      handle({ kpiCategories: [...newOrder, item] }, kWord);
    }
  };

  return (
    <ControlContainer
      fullWidth
      disabled={disabled}
      fullHeight
    >
      <FlexRow style={{ width: '100%', gap: '10px' }}>
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          alignItems: 'center',
        }}
        >
          <LabelController htmlFor={kWord}>Ordem atual</LabelController>
          {oldOrder.map((item) => (
            removeTotal && item === 'Totais' ? null : (
              <Button
                key={`ini-${item}`}
                shape="square"
                size="small"
                variant="contained"
                disabled={newOrder.includes(item)}
                onClick={() => addInSort(item)}
              >
                {item}
              </Button>
            )))}
        </div>
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          alignItems: 'center',
        }}
        >
          <LabelController htmlFor={kWord}>Nova ordem</LabelController>
          {newOrder.map((item) => (
            <Button
              key={`new-${item}`}
              shape="square"
              size="small"
              variant="contained"
              onClick={() => removeFromSort(item)}
            >
              {item}
            </Button>
          ))}
        </div>
      </FlexRow>
    </ControlContainer>
  );
}; */

export const MarkersSelectorController = ({
  handle,
  addKWord,
  removeKWord,
  markersList,
  selectedIndex,
  setSelectedIndex,
  disabled,
}) => {
  const addHandle = () => {
    handle(undefined, addKWord);
    setSelectedIndex((markersList?.length || 1) - 1);
  };

  const deleteHandle = (e, idx) => {
    e.stopPropagation();
    handle(idx, removeKWord);
    setSelectedIndex((markersList?.length || 1) - 1);
  };

  return (
    <ControlContainer
      fullWidth
      disabled={disabled}
      fullHeight
    >
      <FlexRow style={{
        width: '100%', rowGap: '6px', columnGap: '3px', flexWrap: 'wrap',
      }}
      >
        {markersList.map((line, i) => (
          <Badge
            key={`line-${i}-badge`}
            active={selectedIndex === i}
            className={`marker-badge ${selectedIndex === i ? 'marker-badge-active' : ''}`}
            color="secondary"
            size="small"
            onClick={() => setSelectedIndex(i)}
          >
            <span style={{ margin: '0 auto' }}>
              Linha
              {' '}
              {i + 1}
            </span>
            <IconButton
              color={selectedIndex === i ? 'info' : 'secondary'}
              variant="outlined"
              style={{
                padding: '0', height: '15px', width: '15px', boxShadow: 'none',
              }}
              onClick={(e) => deleteHandle(e, i)}
            >
              <Close />
            </IconButton>
          </Badge>
        ))}
        {markersList.length < 4 && (
          <Badge
            className="marker-badge marker-add-badge"
            color="secondary"
            size="small"
            onClick={addHandle}
          >
            <Add />
          </Badge>
        )}
      </FlexRow>
    </ControlContainer>
  );
};

MarkersSelectorController.propTypes = {
  handle: PropTypes.func,
  addKWord: PropTypes.string,
  removeKWord: PropTypes.string,
  markersList: PropTypes.arrayOf(PropTypes.any),
  selectedIndex: PropTypes.number,
  setSelectedIndex: PropTypes.func,
  disabled: PropTypes.bool,
};

MarkersSelectorController.defaultProps = {
  handle: () => {},
  addKWord: '',
  removeKWord: '',
  markersList: [],
  selectedIndex: 0,
  setSelectedIndex: () => {},
  disabled: false,
};
